<script setup lang="ts">
import { useBackToClose } from '@/composables/useBackToClose'
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  X, ShieldCheck, Clock, TrendingUp
} from 'lucide-vue-next'
import type { Auction } from '@/types'
import { useFormatters } from '@/composables/useFormatters'
import { useAuctionStore } from '@/stores/auction'
import { useUserStore } from '@/stores/user'
import { useUIStore } from '@/stores/ui'
import { useFeatureStore } from '@/stores/feature'
import { useI18n } from '@/composables/useI18n'

interface Props {
  modelValue: boolean
  auction: Auction | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'bidPlaced': [amount: number]
}>()

const router = useRouter()
const { currency } = useFormatters()
const { formatMoney } = currency
const { t } = useI18n()
const auctionStore = useAuctionStore()
const userStore = useUserStore()
const uiStore = useUIStore()
const featureStore = useFeatureStore()

const isSubmitting = ref(false)
const customAmount = ref<number | null>(null)

const currentPriceNum = computed(() => {
  if (!props.auction) return 0
  return Number(props.auction.currentPrice.amount) || 0
})

const incrementStep = computed(() => {
  if (!props.auction) return 500
  const step = Number(props.auction.bidIncrement?.amount) || 500
  return step > 0 ? step : 500
})

const minNextBid = computed(() => {
  // First bid may equal the starting price (server rule in placeBidAtomic).
  if (props.auction && (props.auction.bidCount || 0) === 0) {
    return Number(props.auction.startingPrice?.amount) || currentPriceNum.value
  }
  return currentPriceNum.value + incrementStep.value
})

const incrementOptions = computed(() => {
  const step = incrementStep.value
  return [
    { label: `+${step.toLocaleString('ru-RU')} с`, val: step },
    { label: `+${(step * 2).toLocaleString('ru-RU')} с`, val: step * 2 },
    { label: `+${(step * 5).toLocaleString('ru-RU')} с`, val: step * 5 },
    { label: `+${(step * 10).toLocaleString('ru-RU')} с`, val: step * 10 },
  ]
})

const selectedAmount = computed(() => {
  if (customAmount.value && customAmount.value >= minNextBid.value) {
    return customAmount.value
  }
  return minNextBid.value
})

// Hand-rolled dialog: wire Escape to close, as the shared Modal.vue does.
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    customAmount.value = minNextBid.value
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeydown)
  } else {
    document.body.style.overflow = ''
    window.removeEventListener('keydown', onKeydown)
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onKeydown)
})

function close() {
  emit('update:modelValue', false)
}

// Phone back button closes this overlay instead of leaving the page.
useBackToClose(() => props.modelValue, () => close())

const isOwnListing = computed(() => {
  if (!userStore.user || !props.auction) return false
  const sellerId = props.auction.sellerId || (props.auction as any).seller_id || (props.auction.seller as any)?.id
  return sellerId === userStore.user.id
})

function addIncrement(extra: number) {
  customAmount.value = currentPriceNum.value + extra
}

async function handleConfirmBid() {
  if (!props.auction) return
  if (!userStore.isAuthenticated) {
    close()
    router.push({ path: '/login', query: { redirect: `/auctions/${props.auction.id}?action=bid` } })
    return
  }

  if (isOwnListing.value) {
    uiStore.toastWarning(t('common.warning') || 'Эскертүү', t('auction.ownListingNotice') || 'Сиз өзүңүздүн лотуңузга коюм коё албайсыз')
    return
  }

  const bidAmount = selectedAmount.value
  if (bidAmount < minNextBid.value) {
    uiStore.toastError(t('bidSheet.bidErrorTitle') || 'Ошибка ставки', t('bidSheet.bidErrorMin', { min: minNextBid.value.toLocaleString('ru-RU') }) || `Минимальная ставка: ${minNextBid.value.toLocaleString('ru-RU')} сом`)
    return
  }

  isSubmitting.value = true
  try {
    const res = await auctionStore.placeBid(props.auction.id, { amount: String(bidAmount) })
    if (res.success) {
      uiStore.toastSuccess(t('bidSheet.bidSuccessTitle') || 'Ставка принята', t('bidSheet.bidSuccessMsg', { amount: bidAmount.toLocaleString('ru-RU') }) || `Ставка ${bidAmount.toLocaleString('ru-RU')} сом принята! Вы лидируете.`)
      emit('bidPlaced', bidAmount)
      close()
    } else {
      uiStore.toastError(t('bidSheet.bidErrorTitle') || 'Не удалось сделать ставку', res.error || t('common.error'))
    }
  } catch (err: any) {
    uiStore.toastError(t('bidSheet.bidErrorTitle') || 'Ошибка размещения ставки', err?.message || t('common.error'))
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue && auction"
        class="fixed inset-0 z-50 flex flex-col items-center justify-end bg-ink/60 p-0 backdrop-blur-sm sm:justify-center sm:p-4"
        @click.self="close"
      >
        <div
          class="sheet-up flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[1.5rem] border border-border bg-surface shadow-2xl sm:max-h-[85vh] sm:max-w-lg sm:rounded-[1.5rem]"
          role="dialog"
          aria-modal="true"
          :aria-label="t('bidSheet.placeBid') || 'Повысить цену'"
        >
          <!-- Drag handle (mobile) -->
          <div class="mx-auto mb-1 mt-2.5 h-1.5 w-12 shrink-0 rounded-full bg-border sm:hidden" />

          <!-- Header -->
          <div class="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5 sm:py-4">
            <div class="flex min-w-0 items-center gap-2.5">
              <span class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-[11px] font-semibold text-success">
                <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-success live-dot" />
                {{ t('bidSheet.liveBidding') || 'ЖИВЫЕ ТОРГИ' }}
              </span>
              <h3 class="truncate font-display text-[15px] font-semibold text-text-primary sm:text-base">
                {{ t('bidSheet.placeBid') || 'Повысить цену' }}
              </h3>
            </div>
            <button
              type="button"
              class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-text-secondary transition hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              :aria-label="t('aria.closeModal')"
              @click="close"
            >
              <X class="h-4 w-4" />
            </button>
          </div>

          <!-- Body -->
          <div class="space-y-5 overflow-y-auto p-4 sm:p-6">
            <!-- Lot preview -->
            <div class="flex items-center gap-3.5 rounded-xl bg-accent/70 p-3">
              <img
                :src="auction.images[0]"
                :alt="auction.title"
                class="h-16 w-16 shrink-0 rounded-lg bg-accent object-cover"
              />
              <div class="min-w-0 flex-1">
                <h4 class="truncate text-sm font-semibold leading-snug text-text-primary">
                  {{ auction.title }}
                </h4>
                <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
                  <span>{{ t('bidSheet.currentPrice') || 'Текущая:' }} <b class="font-display font-bold tabular text-text-primary">{{ formatMoney(auction.currentPrice) }}</b></span>
                  <span class="inline-flex items-center rounded-full bg-surface px-2 py-0.5 text-[11px] font-semibold tabular text-text-secondary">{{ auction.bidCount }} {{ t('bidSheet.bids') || 'ставок' }}</span>
                </div>
              </div>
            </div>

            <!-- Your bid -->
            <div class="rounded-xl border border-border bg-background p-4 text-center">
              <div class="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
                {{ t('bidSheet.yourBid') || 'Ваша ставка' }}
              </div>
              <div class="mt-1.5 font-display text-3xl font-bold leading-none tabular text-text-primary sm:text-4xl">
                {{ selectedAmount.toLocaleString('ru-RU') }} <span class="text-lg text-primary-hover sm:text-xl">{{ t('common.currency') || 'сом' }}</span>
              </div>
              <div class="mt-2 text-[11px] text-text-muted">
                {{ t('bidSheet.minStep', { step: incrementStep.toLocaleString('ru-RU'), min: minNextBid.toLocaleString('ru-RU') }) || `Минимальный шаг: +${incrementStep.toLocaleString('ru-RU')} сом (следующая мин. ${minNextBid.toLocaleString('ru-RU')} с)` }}
              </div>
            </div>

            <!-- Quick amounts -->
            <div>
              <div class="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
                {{ t('bidSheet.quickOptions') || 'Быстрые варианты:' }}
              </div>
              <div class="grid grid-cols-4 gap-2">
                <button
                  v-for="opt in incrementOptions"
                  :key="opt.label"
                  type="button"
                  class="rounded-full px-1 py-3 text-center text-xs font-semibold tabular transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  :class="selectedAmount === (currentPriceNum + opt.val)
                    ? 'bg-primary/15 text-text-primary ring-1 ring-inset ring-primary/40'
                    : 'bg-accent text-text-secondary hover:bg-accent-hover hover:text-text-primary'"
                  :aria-pressed="selectedAmount === (currentPriceNum + opt.val)"
                  @click="addIncrement(opt.val)"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Custom amount -->
            <div>
              <label for="bid-sheet-amount" class="mb-1.5 block text-xs font-semibold text-text-secondary">
                {{ t('bidSheet.orEnterMaxBid') || 'Или введите свою максимальную ставку:' }}
              </label>
              <div class="relative">
                <input
                  id="bid-sheet-amount"
                  v-model.number="customAmount"
                  type="number"
                  :min="minNextBid"
                  :step="incrementStep"
                  :placeholder="t('bidSheet.bidPlaceholder') || 'Например 45 000'"
                  class="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 pr-14 font-display text-base font-bold tabular text-text-primary placeholder:font-sans placeholder:text-sm placeholder:font-normal placeholder:text-text-muted transition focus:border-ink focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <span class="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-text-muted">{{ t('common.currency') || 'сом' }}</span>
              </div>
            </div>

            <!-- Trust guarantees -->
            <ul class="space-y-2 text-xs leading-relaxed text-text-secondary">
              <li class="flex items-start gap-2">
                <ShieldCheck class="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span>{{ t('bidSheet.escrowGuaranteed') }}</span>
              </li>
              <li class="flex items-start gap-2">
                <Clock class="mt-0.5 h-4 w-4 shrink-0 text-primary-hover" />
                <span>{{ t('bidSheet.antiSnipingGuaranteed', { minutes: featureStore.antiSnipingExtensionMinutes }) }}</span>
              </li>
            </ul>
          </div>

          <!-- Footer action -->
          <div class="shrink-0 space-y-2 border-t border-border bg-background p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-5">
            <div v-if="isOwnListing" class="rounded-xl bg-warning/10 px-3.5 py-3 text-center text-xs font-semibold text-warning">
              {{ t('auction.ownListingNotice') || 'Сиз бул лоттун сатуучусусуз (Өз лотуңуз)' }}
            </div>
            <button
              type="button"
              :disabled="isSubmitting || isOwnListing"
              class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3.5 text-sm font-bold text-onPrimary transition hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              @click="handleConfirmBid"
            >
              <TrendingUp class="h-4 w-4" />
              <span>{{ isOwnListing ? t('lotPage.ownLot') : (t('bidSheet.confirmBidBtn', { amount: selectedAmount.toLocaleString('ru-RU') }) || `Подтвердить цену · ${selectedAmount.toLocaleString('ru-RU')} сом`) }}</span>
            </button>
            <p class="text-center text-[11px] text-text-muted">
              {{ t('bidSheet.termsAgreeNotice') || 'Нажимая кнопку, вы соглашаетесь с правилами проведения аукционов iTorgo' }}
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes sheetUp {
  from { transform: translateY(24px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
.sheet-up {
  animation: sheetUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>
